'use client';
import React, { useState } from 'react';
import Toggle from '@/components/ui/Toggle';
import Icon from '@/components/ui/AppIcon';

type AgeBand = '5-7' | '8-10' | '11-13';

const ageBands: { id: AgeBand; label: string }[] = [
  { id: '5-7', label: '5–7 years' },
  { id: '8-10', label: '8–10 years' },
  { id: '11-13', label: '11–13 years' },
];

const contentTypes = [
  { id: 'ct-wildlife', label: 'Wildlife Sightings', desc: 'Whales, dolphins, fish schools', defaultOn: true },
  { id: 'ct-environment', label: 'Ocean Environment', desc: 'Currents, temperature, depth', defaultOn: true },
  { id: 'ct-iceshelf', label: 'Ice Shelf (gentle)', desc: 'Heartbeat pulse — no calving', defaultOn: true },
  { id: 'ct-guardian', label: 'Guardian Mode', desc: 'Care missions and blessings', defaultOn: true },
  { id: 'ct-learning', label: 'Learning Cards', desc: 'Fact pop-ups and quizzes', defaultOn: true },
  { id: 'ct-bathymetry', label: 'Seafloor Maps', desc: 'Glowing topography (11–13 only)', defaultOn: false },
];

const sessionLimits = [
  { id: 'sl-30', label: '30 min' },
  { id: 'sl-60', label: '1 hour' },
  { id: 'sl-90', label: '90 min' },
  { id: 'sl-unlimited', label: 'Unlimited' },
];

export default function ContentFilterPanel() {
  const [selectedBand, setSelectedBand] = useState<AgeBand>('8-10');
  const [toggles, setToggles] = useState<Record<string, boolean>>(
    Object.fromEntries(contentTypes.map((c) => [c.id, c.defaultOn]))
  );
  const [sessionLimit, setSessionLimit] = useState('sl-60');

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="FunnelIcon" size={16} className="text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">Content Filters</h2>
      </div>

      {/* Age band */}
      <div className="mb-5">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Age Band</p>
        <div className="flex gap-1.5">
          {ageBands.map((band) => (
            <button
              key={band.id}
              onClick={() => setSelectedBand(band.id)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                selectedBand === band.id
                  ? 'bg-primary/10 text-primary border border-primary/20' :'bg-muted text-muted-foreground hover:text-foreground border border-border'
              }`}
            >
              {band.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content toggles */}
      <div className="mb-5">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">Content Types</p>
        <div className="space-y-3">
          {contentTypes.map((ct) => (
            <div key={ct.id} className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground">{ct.label}</p>
                <p className="text-[10px] text-muted-foreground truncate">{ct.desc}</p>
              </div>
              <Toggle
                size="sm"
                checked={toggles[ct.id]}
                onChange={(v) => setToggles((prev) => ({ ...prev, [ct.id]: v }))}
                disabled={ct.id === 'ct-bathymetry' && selectedBand !== '11-13'}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Session limit */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Daily Session Limit</p>
        <div className="grid grid-cols-4 gap-1.5">
          {sessionLimits.map((sl) => (
            <button
              key={sl.id}
              onClick={() => setSessionLimit(sl.id)}
              className={`py-1.5 rounded-lg text-[11px] font-semibold transition-all duration-150 ${
                sessionLimit === sl.id
                  ? 'bg-primary/10 text-primary border border-primary/20' :'bg-muted text-muted-foreground hover:text-foreground border border-border'
              }`}
            >
              {sl.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">Break reminder every 20 minutes</p>
      </div>

      <button className="w-full mt-4 py-2 bg-primary/10 border border-primary/20 text-primary text-xs font-semibold rounded-lg hover:bg-primary/20 transition-colors duration-150">
        Save Filter Settings
      </button>
    </div>
  );
}